import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Flex,
  Heading,
  Icon,
  Icons,
  Paragraph,
  Span,
} from "@ir/ui";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";
import { AssociateBrandRow } from "./AssociateBrandRow";
import { AssociatedBrandsTable } from "./AssociatedBrandsTable";
import type { PurchaserBrandAssociation } from "@src/domain/entities/purchaser";

interface AssociatedBrandsSectionProps {
  purchaserId: number;
}

export const AssociatedBrandsSection = ({
  purchaserId,
}: AssociatedBrandsSectionProps) => {
  const { purchaserBrands, applyBrandAssociationUpdate } = usePurchasersData();

  const [isAssociateFormOpen, setIsAssociateFormOpen] = useState(false);
  const [isAssociateSaving, setIsAssociateSaving] = useState(false);

  const brands = useMemo(
    () => purchaserBrands[purchaserId] ?? [],
    [purchaserBrands, purchaserId],
  );
  const assignedBrandIds = useMemo(
    () => new Set(brands.map((brand) => brand.brandId)),
    [brands],
  );

  const handleAssociationUpdated = useCallback(
    (association: PurchaserBrandAssociation) => {
      applyBrandAssociationUpdate(purchaserId, association);
    },
    [applyBrandAssociationUpdate, purchaserId],
  );

  const brandButton = (
    <HasAnyPermission requiredPermissions={[Permissions.PURCHASERS_EDIT]}>
      <Button
        size="small"
        variant="outline"
        onPress={() => setIsAssociateFormOpen(true)}
        isDisabled={isAssociateFormOpen || isAssociateSaving}
      >
        <Icon name={Icons.Plus} slot="start" />
        Brand
      </Button>
    </HasAnyPermission>
  );

  if (brands.length === 0) {
    if (isAssociateFormOpen) {
      return (
        <AssociateBrandRow
          purchaserId={purchaserId}
          assignedBrandIds={assignedBrandIds}
          isSaving={isAssociateSaving}
          onCancel={() => setIsAssociateFormOpen(false)}
          onSaveStart={() => setIsAssociateSaving(true)}
          onSaveEnd={() => {
            setIsAssociateSaving(false);
          }}
        />
      );
    }

    return (
      <Card className="border-1 border-color-grey-25 w-100">
        <CardHeader className="border-bottom-1 border-color-grey-25">
          <Flex align="center" justifyContent="space-between">
            <Heading level={6} className="text-grey-700 font-bold m-0">
              Associated Brands
            </Heading>
            {brandButton}
          </Flex>
        </CardHeader>
        <CardContent className="p-3">
          <Flex className="w-100" align="center" justifyContent="center">
            <EmptyState
              icon={Icons.InfoCircle}
              title="No brands associated yet."
              description={
                <Paragraph className="text-grey-500 font-regular mb-0">
                  Add a brand to get started.
                </Paragraph>
              }
              className="bg-white p-4"
            />
          </Flex>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-1 border-color-grey-25 w-100">
      <CardHeader className="border-bottom-1 border-color-grey-25">
        <Flex align="center" justifyContent="space-between">
          <Heading level={6} className="text-grey-700 font-bold m-0">
            <Span className="pe-2">
              Associated Brand{brands.length > 1 ? "s" : ""}
            </Span>
            <Span className="text-grey-500 font-regular text-xs ml-2">
              {brands.filter((brand) => brand.isActive).length} / {brands.length}
            </Span>
          </Heading>
          {brandButton}
        </Flex>
      </CardHeader>
      <CardContent className="p-3 pb-0">
        {isAssociateFormOpen ? (
          <AssociateBrandRow
            purchaserId={purchaserId}
            assignedBrandIds={assignedBrandIds}
            isSaving={isAssociateSaving}
            onCancel={() => setIsAssociateFormOpen(false)}
            onSaveStart={() => setIsAssociateSaving(true)}
            onSaveEnd={() => {
              setIsAssociateSaving(false);
            }}
          />
        ) : null}

        <AssociatedBrandsTable
          purchaserId={purchaserId}
          brands={brands}
          onAssociationUpdated={handleAssociationUpdated}
        />
      </CardContent>
    </Card>
  );
};
